import { Button, Collapsible, CollapsibleContent, CollapsibleTrigger } from '@pagespeedify/ui';
import { CheckCircle2, ChevronDown, Loader2, AlertTriangle, CircleDashed, ShieldCheck } from 'lucide-react';
import { cn } from '@pagespeedify/ui';
import { useMemo } from 'react';
import type { ClarificationActionMode, VerificationUpdateProps, VerificationUpdateAction, VerificationUpdateCheck } from '../../types/json-render';

interface VerificationUpdateComponentProps extends VerificationUpdateProps {
    onSubmit?: (option: VerificationUpdateAction) => void;
    isLoading?: boolean;
    loadingActionId?: string | null;
    activeActionIds?: string[];
    forceDisabled?: boolean;
    disabledReason?: string;
    actionMode?: ClarificationActionMode;
}

function normalizeActionLabel(action: VerificationUpdateAction): string {
    const normalizedValue = String(action.value || '').trim().toLowerCase();
    if (normalizedValue === 'skip_verification_and_create_pr') return 'Create PR';
    if (normalizedValue === 'skip_verification_and_create_pr_and_continue_to_next_plan') return 'Create PR & Continue to next plan';
    if (normalizedValue === 'skip_verification_and_create_pr_and_continue_to_next_batch') return 'Create PR & Continue to next batch';
    if (normalizedValue === 'find_more_issues') return 'Find more issues';
    if (normalizedValue === 'continue_with_new_goal') return 'Change goal';
    const rawLabel = String(action.label || '').trim();
    return rawLabel
        .replace(/\s*\([^)]*\)\s*$/i, '')
        .replace(/\s*[\u2022\u00b7\u2027]\s*.*$/i, '')
        .trim();
}

function isVerificationOverrideAction(action: VerificationUpdateAction): boolean {
    return action.verificationOverride === true
        || String(action.value || '').trim().toLowerCase().startsWith('skip_verification_and_create_pr');
}

function simplifyVerificationCopy(value?: string | null, fallback = 'checks need setup'): string {
    const raw = String(value || '').trim();
    const haystack = raw.toLowerCase();
    if (!raw) return fallback;
    if (
        haystack.includes('preparing local')
        || haystack.includes('local tests verifier')
        || haystack.includes('bootstrap')
        || haystack.includes('pytest')
        || haystack.includes('test setup')
        || haystack.includes('tests need setup')
    ) {
        return 'tests need setup';
    }
    if (
        haystack.includes('python is unavailable')
        || haystack.includes('python runtime')
        || haystack.includes('runtime_unavailable')
        || haystack.includes('runtime unavailable')
    ) {
        return 'Python is unavailable';
    }
    if (haystack.includes('syntax')) return 'syntax failed';
    if (haystack.includes('build')) return 'build failed';
    if (haystack.includes('lint')) return 'lint needs setup';
    if (haystack.includes('type')) return 'type checks need setup';
    return raw.length <= 80 ? raw : fallback;
}

function formatCheckLine(check: VerificationUpdateCheck): string {
    const label = String(check.label || check.kind || '').trim() || 'Check';
    switch (check.state) {
        case 'passed':
            return `${label} passed`;
        case 'running':
            return `${label} running`;
        case 'failed':
            return `${label} failed`;
        case 'blocked':
            return `${label} blocked`;
        case 'deferred':
            return `${label} deferred to CI`;
        case 'skipped':
        default:
            return `${label} not applicable`;
    }
}

function stateIcon(state: VerificationUpdateCheck['state']) {
    switch (state) {
        case 'passed':
            return <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 text-emerald-400" />;
        case 'running':
            return <Loader2 className="mt-0.5 h-3.5 w-3.5 text-blue-400 animate-spin" />;
        case 'failed':
        case 'blocked':
            return <AlertTriangle className="mt-0.5 h-3.5 w-3.5 text-orange-400" />;
        case 'deferred':
            return <CircleDashed className="mt-0.5 h-3.5 w-3.5 text-sky-400" />;
        case 'skipped':
        default:
            return <CircleDashed className="mt-0.5 h-3.5 w-3.5 text-muted-foreground" />;
    }
}

function formatRawLabel(value?: string | null): string {
    const normalized = String(value || '').trim();
    if (!normalized) return '';
    return normalized
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (ch) => ch.toUpperCase());
}

export default function VerificationUpdate({
    lifecycle,
    gateStatus,
    confidenceLevel,
    confidenceReason,
    severity,
    headline,
    body,
    highlightNote,
    safetyNotes,
    checks,
    scope,
    provenance,
    environmentNotes,
    replayCommands,
    actions,
    detailsDefaultOpen = false,
    onSubmit,
    isLoading = false,
    loadingActionId = null,
    activeActionIds = [],
    forceDisabled = false,
    disabledReason,
    actionMode = 'full'
}: VerificationUpdateComponentProps) {
    const activeSet = new Set(activeActionIds);
    const now = Date.now();
    const normalizedChecks = Array.isArray(checks) ? checks : [];
    const visibleChecks = useMemo(() => {
        return normalizedChecks
            .filter((check) => check.state !== 'skipped')
            .slice(0, 5);
    }, [normalizedChecks]);
    const normalizedActions = Array.isArray(actions) ? actions : [];
    const checkCounts = normalizedChecks.reduce<Record<string, number>>((acc, check) => {
        acc[check.state] = (acc[check.state] || 0) + 1;
        return acc;
    }, {});
    const hasAttention = (checkCounts.failed || 0) > 0
        || (checkCounts.blocked || 0) > 0
        || String(severity || '').trim().toLowerCase() === 'critical';
    const normalizedLifecycle = String(lifecycle || '').trim().toLowerCase();
    const isRunningVerification = ['running', 'repairing', 'rerunning'].includes(normalizedLifecycle);
    const compactCounts = [
        checkCounts.passed ? `${checkCounts.passed} passed` : '',
        checkCounts.failed ? `${checkCounts.failed} failed` : '',
        checkCounts.blocked ? `${checkCounts.blocked} blocked` : '',
        checkCounts.deferred ? `${checkCounts.deferred} deferred` : ''
    ].filter(Boolean);
    const statusSummary = (() => {
        switch (String(lifecycle || '').trim().toLowerCase()) {
            case 'repairing':
                return 'Verification repair is running.';
            case 'rerunning':
                return 'Rerunning verification after a repair.';
            case 'running':
                return 'Running local verification.';
            case 'awaiting_ci':
                return 'Local verification completed successfully. Remaining checks will run in CI.';
            case 'failed':
                return 'Local verification found issues that still need attention.';
            case 'blocked':
                return 'Local verification could not continue automatically.';
            case 'passed':
                return 'This change passed all available local verification checks.';
            default:
                if (String(gateStatus || '').trim().toLowerCase() === 'awaiting_ci') {
                    return 'Local verification completed successfully. Remaining checks will run in CI.';
                }
                if (String(gateStatus || '').trim().toLowerCase() === 'repair_required') {
                    return 'Local verification did not complete cleanly.';
                }
                return 'Verification update available.';
        }
    })();

    return (
        <div className="rounded-lg bg-card p-3">
            <div className="space-y-3">
                <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 space-y-1">
                        <div className="flex items-center gap-2">
                            {isRunningVerification ? (
                                <Loader2 className="h-4 w-4 shrink-0 animate-spin text-primary" />
                            ) : hasAttention ? (
                                <AlertTriangle className="h-4 w-4 shrink-0 text-amber-400" />
                            ) : (
                                <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-400" />
                            )}
                            <p className="truncate text-sm font-semibold text-foreground">
                                {headline || 'Verification'}
                            </p>
                        </div>
                        <p className="text-sm text-muted-foreground">{body || statusSummary}</p>
                    </div>
                    {(gateStatus || confidenceLevel) && (
                        <div className="flex shrink-0 flex-col items-end gap-1 text-xs">
                            {gateStatus && (
                                <span className="rounded border border-border/70 bg-muted/40 px-2 py-0.5 text-foreground">
                                    {formatRawLabel(gateStatus)}
                                </span>
                            )}
                            {confidenceLevel && (
                                <span className="text-muted-foreground">
                                    {formatRawLabel(confidenceLevel)} confidence
                                </span>
                            )}
                        </div>
                    )}
                </div>

                {compactCounts.length > 0 && (
                    <div className="flex flex-wrap gap-2 text-xs">
                        {compactCounts.map((entry) => (
                            <span key={entry} className="rounded-md bg-muted/40 px-2 py-1 text-foreground">
                                {entry}
                            </span>
                        ))}
                    </div>
                )}

                {visibleChecks.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                        {visibleChecks.map((check) => (
                            <span
                                key={`${check.kind}-${check.state}`}
                                className="inline-flex items-center gap-1 rounded-full border border-border/70 bg-background px-2 py-1 text-[11px] text-foreground"
                            >
                                {stateIcon(check.state)}
                                {formatCheckLine(check)}
                            </span>
                        ))}
                    </div>
                )}

                {highlightNote && (
                    <div className="rounded-md border border-orange-500/20 bg-orange-500/5 px-3 py-2 text-xs text-foreground">
                        {highlightNote}
                    </div>
                )}

                {onSubmit && normalizedActions.length > 0 && actionMode === 'full' && (
                    <div className="flex flex-wrap gap-2">
                        {normalizedActions.map((action, index) => {
                            const expiresAtMs = action.expiresAt ? new Date(action.expiresAt).getTime() : Number.NaN;
                            const isExpired = Number.isFinite(expiresAtMs) && expiresAtMs <= now;
                            const isInactive = !!action.actionId && activeSet.size > 0 && !activeSet.has(action.actionId);
                            const disabled = forceDisabled || isLoading || action.disabled === true || isInactive || isExpired;
                            const isClickedAction = !!loadingActionId && !!action.actionId && loadingActionId === action.actionId;
                            const verificationOverride = isVerificationOverrideAction(action);
                            const verificationOverrideReason = `Skip verification: ${simplifyVerificationCopy(
                                action.verificationOverrideReason
                                || confidenceReason
                                || body
                            )}`;
                            return (
                                <Button
                                    key={`${action.actionId || action.value}-${index}`}
                                    variant={action.variant || 'outline'}
                                    size="sm"
                                    onClick={() => {
                                        if (disabled) return;
                                        onSubmit(action);
                                    }}
                                    disabled={disabled}
                                    title={
                                        forceDisabled
                                            ? (disabledReason || 'Action is no longer available.')
                                            : (
                                                action.disabled
                                                    ? (action.disabledReason || 'This action is currently unavailable.')
                                                    : ((isInactive || isExpired) ? 'Updating options...' : (verificationOverride ? verificationOverrideReason : undefined))
                                            )
                                    }
                                >
                                    {isClickedAction ? (
                                        <>
                                            <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                                            Processing...
                                        </>
                                    ) : (
                                        <>
                                            {normalizeActionLabel(action)}
                                            {verificationOverride && (
                                                <AlertTriangle
                                                    className="ml-1 h-3.5 w-3.5 text-orange-400"
                                                    aria-hidden="true"
                                                />
                                            )}
                                        </>
                                    )}
                                </Button>
                            );
                        })}
                    </div>
                )}

                <Collapsible defaultOpen={detailsDefaultOpen}>
                    <CollapsibleTrigger asChild>
                        <button
                            type="button"
                            className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
                        >
                            Verification details
                            <ChevronDown className="h-3.5 w-3.5" />
                        </button>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="mt-3 space-y-3 rounded-lg border border-border/60 bg-muted/20 p-3">
                        <div className="space-y-1 text-xs text-foreground">
                            <div><span className="font-medium">Verification state:</span> {String(lifecycle || 'unknown').replace(/_/g, ' ')}</div>
                            {confidenceLevel && (
                                <div><span className="font-medium">Confidence:</span> {String(confidenceLevel).replace(/_/g, ' ')}</div>
                            )}
                            {severity && (
                                <div><span className="font-medium">Severity:</span> {String(severity)}</div>
                            )}
                            {gateStatus && (
                                <div><span className="font-medium">Gate status:</span> {String(gateStatus).replace(/_/g, ' ')}</div>
                            )}
                            {confidenceReason && (
                                <div><span className="font-medium">Reason:</span> {confidenceReason}</div>
                            )}
                        </div>

                        {Array.isArray(safetyNotes) && safetyNotes.length > 0 && (
                            <div className="space-y-1 text-xs text-foreground">
                                <div className="font-medium text-muted-foreground">Safety notes</div>
                                {safetyNotes.map((note) => (
                                    <div key={note} className="flex items-start gap-2">
                                        <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-400" />
                                        <span>{note}</span>
                                    </div>
                                ))}
                            </div>
                        )}

                        {normalizedChecks.length > 0 && (
                            <div className="space-y-1 text-xs text-foreground">
                                <div className="font-medium text-muted-foreground">Checks</div>
                                {normalizedChecks.map((check) => (
                                    <div key={`detail-${check.kind}`} className="flex items-start gap-2">
                                        {stateIcon(check.state)}
                                        <span>
                                            {formatCheckLine(check)}
                                            {check.reason ? `: ${check.reason}` : ''}
                                        </span>
                                    </div>
                                ))}
                                {Array.isArray(scope) && scope.length > 0 && (
                                    <div className="pt-1 text-muted-foreground">
                                        {scope.join(' / ')}
                                    </div>
                                )}
                            </div>
                        )}

                        {Array.isArray(environmentNotes) && environmentNotes.length > 0 && (
                            <div className="space-y-1 text-xs text-foreground">
                                <div className="font-medium text-muted-foreground">Environment</div>
                                {environmentNotes.map((note) => (
                                    <div key={note}>{note}</div>
                                ))}
                            </div>
                        )}

                        {Array.isArray(provenance) && provenance.length > 0 && (
                            <div className="space-y-1 text-xs text-foreground">
                                <div className="font-medium text-muted-foreground">Verification provenance</div>
                                {provenance.map((note) => (
                                    <div key={note}>{note}</div>
                                ))}
                            </div>
                        )}

                        {Array.isArray(replayCommands) && replayCommands.length > 0 && (
                            <div className="space-y-2 text-xs text-foreground">
                                <div className="font-medium text-muted-foreground">Replay commands</div>
                                <div className="space-y-1">
                                    {replayCommands.map((command) => (
                                        <pre
                                            key={command}
                                            className={cn(
                                                'overflow-x-auto rounded-md border border-border/60 bg-background px-3 py-2 font-mono text-[11px]'
                                            )}
                                        >
                                            {command}
                                        </pre>
                                    ))}
                                </div>
                            </div>
                        )}
                    </CollapsibleContent>
                </Collapsible>
            </div>
        </div>
    );
}
